import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useAuth } from '@/context/AuthContext';
import { registarAlteracao, registarAlteracaoDetalhada, type EntidadeAuditada } from '@/utils/auditoria';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Formacao {
  id: string;
  municipio_id: string | null;
  nome: string;
  entidade_formadora: string | null;
  categoria: string | null;
  modalidade: string;
  data_inicio: string | null;
  data_fim: string | null;
  duracao_horas: number;
  local: string | null;
  descricao: string | null;
  estado: string;
  custo: number;
  orcamento_previsto: number;
  financiamento: string | null;
  observacoes: string | null;
  formador_interno_id: string | null;
  created_at: string;
  updated_at: string;
  // virtual (count from join)
  total_participantes?: number;
}

export interface FormacaoParticipante {
  id: string;
  formacao_id: string;
  funcionario_id: string;
  funcionario_nome: string | null;
  estado_participacao: string;
  nota_final: string | null;
  certificado_emitido: boolean;
  data_certificado: string | null;
  observacoes: string | null;
  avaliacao_nota: number | null;
  avaliacao_comentario: string | null;
  avaliacao_data: string | null;
  certificado_url: string | null;
  custo: number | null;
  created_at: string;
}

export interface FormacaoDocumento {
  id: string;
  formacao_id: string;
  nome: string;
  tipo: string;
  url: string | null;
  tamanho: number | null;
  criado_por: string | null;
  created_at: string;
}

export interface FormacaoFormador {
  id: string;
  municipio_id: string | null;
  funcionario_id: string;
  nome: string;
  email: string | null;
  telefone: string | null;
  areas_formacao: string[] | null;
  certificacoes: string | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

export type NovaFormacaoInput = Omit<Formacao, 'id' | 'created_at' | 'updated_at' | 'total_participantes'>;
export type EditarFormacaoInput = Partial<NovaFormacaoInput>;

export type NovoParticipanteInput = Omit<FormacaoParticipante, 'id' | 'created_at'>;

export interface UploadDocumentoInput {
  formacao_id: string;
  nome: string;
  tipo: string;
  file: File;
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface FormacaoContextValue {
  formacoes: Formacao[];
  participantes: FormacaoParticipante[];
  documentos: FormacaoDocumento[];
  formadores: FormacaoFormador[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  // formações
  criarFormacao: (data: NovaFormacaoInput) => Promise<Formacao | null>;
  editarFormacao: (id: string, data: EditarFormacaoInput) => Promise<boolean>;
  eliminarFormacao: (id: string) => Promise<boolean>;

  // participantes
  adicionarParticipante: (data: NovoParticipanteInput) => Promise<FormacaoParticipante | null>;
  atualizarParticipante: (id: string, data: Partial<FormacaoParticipante>) => Promise<boolean>;
  removerParticipante: (id: string) => Promise<boolean>;

  // participantes de uma formação específica
  participantesDaFormacao: (formacaoId: string) => FormacaoParticipante[];
  documentosDaFormacao: (formacaoId: string) => FormacaoDocumento[];

  // documentos
  uploadDocumento: (input: UploadDocumentoInput) => Promise<FormacaoDocumento | null>;
  eliminarDocumento: (doc: FormacaoDocumento) => Promise<boolean>;
  getDocumentoUrl: (path: string) => string;

  // certificados de participantes
  uploadCertificadoParticipante: (participanteId: string, formacaoId: string, file: File) => Promise<string | null>;
  removerCertificadoParticipante: (participanteId: string, url: string) => Promise<boolean>;

  refresh: () => Promise<void>;
}

const FormacaoContext = createContext<FormacaoContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function FormacaoProvider({ children }: { children: ReactNode }) {
  const { activeMunicipio } = useActiveMunicipio();
  const { profile } = useAuth();
  const [formacoes, setFormacoes] = useState<Formacao[]>([]);
  const [participantes, setParticipantes] = useState<FormacaoParticipante[]>([]);
  const [documentos, setDocumentos] = useState<FormacaoDocumento[]>([]);
  const [formadores, setFormadores] = useState<FormacaoFormador[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const isFormacaoModule =
    activeMunicipio?.sigla === 'GF' ||
    activeMunicipio?.nome === 'Gestão da Formação';

  const userEmail = profile?.email ?? null;

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fRes, pRes, dRes, formadoresRes] = await Promise.all([
        supabase
          .from('formacoes')
          .select('*')
          .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
          .order('data_inicio', { ascending: false }),
        supabase
          .from('formacao_participantes')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('formacao_documentos')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('formacao_formadores')
          .select('*')
          .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
          .eq('ativo', true)
          .order('nome', { ascending: true }),
      ]);

      if (fRes.error) throw fRes.error;
      if (pRes.error) throw pRes.error;
      if (dRes.error) throw dRes.error;
      if (!formadoresRes.error) {
        setFormadores(formadoresRes.data as FormacaoFormador[] ?? []);
      }

      const fs: Formacao[] = (fRes.data ?? []).map((r) => ({
        ...r,
        duracao_horas: Number(r.duracao_horas ?? 0),
        custo: Number(r.custo ?? 0),
        orcamento_previsto: Number(r.orcamento_previsto ?? 0),
        formador_interno_id: r.formador_interno_id ?? null,
      }));

      const countMap: Record<string, number> = {};
      (pRes.data ?? []).forEach((p) => {
        if (p.formacao_id) {
          countMap[p.formacao_id] = (countMap[p.formacao_id] ?? 0) + 1;
        }
      });
      fs.forEach((f) => { f.total_participantes = countMap[f.id] ?? 0; });

      setFormacoes(fs);
      setParticipantes(pRes.data as FormacaoParticipante[] ?? []);
      setDocumentos(dRes.data as FormacaoDocumento[] ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isFormacaoModule) return;
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadAll();
  }, [isFormacaoModule, loadAll]);

  // ── CRUD Formações ──────────────────────────────────────────────────────────

  const criarFormacao = useCallback(async (data: NovaFormacaoInput): Promise<Formacao | null> => {
    setSaving(true);
    try {
      const payload = { ...data, municipio_id: MUNICIPIO_FORMACAO_ID };
      const { data: row, error: err } = await supabase
        .from('formacoes')
        .insert(payload)
        .select()
        .maybeSingle();
      if (err) throw err;
      if (!row) return null;
      const nova: Formacao = { ...row, duracao_horas: Number(row.duracao_horas), custo: Number(row.custo), orcamento_previsto: Number(row.orcamento_previsto ?? 0), total_participantes: 0 };
      setFormacoes((prev) => [nova, ...prev]);

      await registarAlteracao(
        'formacao' as EntidadeAuditada,
        nova.id,
        nova.nome,
        'criacao',
        `Formação "${nova.nome}" criada (${nova.modalidade}).`,
        { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return nova;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [userEmail]);

  const editarFormacao = useCallback(async (id: string, data: EditarFormacaoInput): Promise<boolean> => {
    setSaving(true);
    try {
      const anterior = formacoes.find((f) => f.id === id);
      const { error: err } = await supabase
        .from('formacoes')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (err) throw err;
      setFormacoes((prev) => prev.map((f) => f.id === id ? { ...f, ...data } : f));

      if (anterior) {
        const dbAnterior = { ...anterior } as Record<string, unknown>;
        const dbNovo = { ...anterior, ...data } as Record<string, unknown>;
        await registarAlteracaoDetalhada(
          'formacao' as EntidadeAuditada,
          id,
          anterior.nome,
          'edicao',
          dbAnterior,
          dbNovo,
          { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail, camposIgnorados: ['id', 'created_at', 'updated_at', 'municipio_id', 'total_participantes'] }
        );
      } else {
        await registarAlteracao(
          'formacao' as EntidadeAuditada,
          id,
          data.nome ?? id,
          'edicao',
          `Formação ${data.nome ?? id} atualizada.`,
          { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
        );
      }

      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [formacoes, userEmail]);

  const eliminarFormacao = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const anterior = formacoes.find((f) => f.id === id);
      const nome = anterior?.nome ?? id;
      const totalPart = anterior?.total_participantes ?? 0;

      const { error: err } = await supabase.from('formacoes').delete().eq('id', id);
      if (err) throw err;
      setFormacoes((prev) => prev.filter((f) => f.id !== id));
      setParticipantes((prev) => prev.filter((p) => p.formacao_id !== id));
      setDocumentos((prev) => prev.filter((d) => d.formacao_id !== id));

      await registarAlteracao(
        'formacao' as EntidadeAuditada,
        id,
        nome,
        'eliminacao',
        `Formação "${nome}" eliminada${totalPart > 0 ? ` (com ${totalPart} participantes)` : ''}.`,
        { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [formacoes, userEmail]);

  // ── CRUD Participantes ──────────────────────────────────────────────────────

  const adicionarParticipante = useCallback(async (data: NovoParticipanteInput): Promise<FormacaoParticipante | null> => {
    setSaving(true);
    try {
      const { data: row, error: err } = await supabase
        .from('formacao_participantes')
        .insert(data)
        .select()
        .maybeSingle();
      if (err) throw err;
      if (!row) return null;
      const novo = row as FormacaoParticipante;
      setParticipantes((prev) => [novo, ...prev]);
      setFormacoes((prev) => prev.map((f) =>
        f.id === data.formacao_id
          ? { ...f, total_participantes: (f.total_participantes ?? 0) + 1 }
          : f
      ));

      const formacaoNome = formacoes.find((f) => f.id === data.formacao_id)?.nome ?? data.formacao_id;
      await registarAlteracao(
        'participante' as EntidadeAuditada,
        novo.id,
        novo.funcionario_nome ?? novo.funcionario_id,
        'criacao',
        `Participante "${novo.funcionario_nome ?? novo.funcionario_id}" adicionado à formação "${formacaoNome}".`,
        { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return novo;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [formacoes, userEmail]);

  const atualizarParticipante = useCallback(async (id: string, data: Partial<FormacaoParticipante>): Promise<boolean> => {
    setSaving(true);
    try {
      const anterior = participantes.find((p) => p.id === id);
      const { error: err } = await supabase.from('formacao_participantes').update(data).eq('id', id);
      if (err) throw err;
      setParticipantes((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));

      if (anterior) {
        const dbAnterior = { ...anterior } as Record<string, unknown>;
        const dbNovo = { ...anterior, ...data } as Record<string, unknown>;
        await registarAlteracaoDetalhada(
          'participante' as EntidadeAuditada,
          id,
          anterior.funcionario_nome ?? anterior.funcionario_id,
          'edicao',
          dbAnterior,
          dbNovo,
          { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail, camposIgnorados: ['id', 'created_at', 'formacao_id', 'funcionario_id'] }
        );
      }

      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [participantes, userEmail]);

  const removerParticipante = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const p = participantes.find((x) => x.id === id);
      const { error: err } = await supabase.from('formacao_participantes').delete().eq('id', id);
      if (err) throw err;
      setParticipantes((prev) => prev.filter((x) => x.id !== id));
      if (p) {
        setFormacoes((prev) => prev.map((f) =>
          f.id === p.formacao_id
            ? { ...f, total_participantes: Math.max(0, (f.total_participantes ?? 1) - 1) }
            : f
        ));
        const formacaoNome = formacoes.find((f) => f.id === p.formacao_id)?.nome ?? p.formacao_id;
        await registarAlteracao(
          'participante' as EntidadeAuditada,
          id,
          p.funcionario_nome ?? p.funcionario_id,
          'eliminacao',
          `Participante "${p.funcionario_nome ?? p.funcionario_id}" removido da formação "${formacaoNome}".`,
          { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
        );
      }
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [participantes, formacoes, userEmail]);

  // ── CRUD Documentos ────────────────────────────────────────────────────────

  const uploadDocumento = useCallback(async (input: UploadDocumentoInput): Promise<FormacaoDocumento | null> => {
    setSaving(true);
    try {
      const ext = input.file.name.split('.').pop() ?? 'bin';
      const path = `${input.formacao_id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('formacao-documentos')
        .upload(path, input.file, { upsert: false });
      if (upErr) throw upErr;

      const { data: row, error: dbErr } = await supabase
        .from('formacao_documentos')
        .insert({
          formacao_id: input.formacao_id,
          nome: input.nome,
          tipo: input.tipo,
          url: path,
          tamanho: input.file.size,
        })
        .select()
        .maybeSingle();
      if (dbErr) throw dbErr;
      if (!row) return null;
      const novo = row as FormacaoDocumento;
      setDocumentos((prev) => [novo, ...prev]);

      const formacaoNome = formacoes.find((f) => f.id === input.formacao_id)?.nome ?? input.formacao_id;
      await registarAlteracao(
        'documento' as EntidadeAuditada,
        novo.id,
        input.nome,
        'criacao',
        `Documento "${input.nome}" (${(input.file.size / 1024).toFixed(1)} KB) adicionado à formação "${formacaoNome}".`,
        { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return novo;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [formacoes, userEmail]);

  const eliminarDocumento = useCallback(async (doc: FormacaoDocumento): Promise<boolean> => {
    setSaving(true);
    try {
      if (doc.url) {
        await supabase.storage.from('formacao-documentos').remove([doc.url]);
      }
      const { error: err } = await supabase.from('formacao_documentos').delete().eq('id', doc.id);
      if (err) throw err;
      setDocumentos((prev) => prev.filter((d) => d.id !== doc.id));

      const formacaoNome = formacoes.find((f) => f.id === doc.formacao_id)?.nome ?? doc.formacao_id;
      await registarAlteracao(
        'documento' as EntidadeAuditada,
        doc.id,
        doc.nome,
        'eliminacao',
        `Documento "${doc.nome}" eliminado da formação "${formacaoNome}".`,
        { municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [formacoes, userEmail]);

  const getDocumentoUrl = useCallback((path: string): string => {
    const { data } = supabase.storage.from('formacao-documentos').getPublicUrl(path);
    return data.publicUrl;
  }, []);

  // ── Certificados de Participantes ──────────────────────────────────────────

  const uploadCertificadoParticipante = useCallback(async (
    participanteId: string,
    formacaoId: string,
    file: File,
  ): Promise<string | null> => {
    setSaving(true);
    try {
      const ext = file.name.split('.').pop() ?? 'pdf';
      const path = `certificados/${formacaoId}/${participanteId}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('formacao-documentos')
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { error: dbErr } = await supabase
        .from('formacao_participantes')
        .update({
          certificado_url: path,
          certificado_emitido: true,
          data_certificado: new Date().toISOString().split('T')[0],
        })
        .eq('id', participanteId);
      if (dbErr) throw dbErr;
      setParticipantes((prev) =>
        prev.map((p) =>
          p.id === participanteId
            ? { ...p, certificado_url: path, certificado_emitido: true, data_certificado: new Date().toISOString().split('T')[0] }
            : p
        )
      );

      const p = participantes.find((x) => x.id === participanteId);
      const formacaoNome = formacoes.find((f) => f.id === formacaoId)?.nome ?? formacaoId;
      await registarAlteracao(
        'participante' as EntidadeAuditada,
        participanteId,
        p?.funcionario_nome ?? participanteId,
        'edicao',
        `Certificado emitido para "${p?.funcionario_nome ?? participanteId}" na formação "${formacaoNome}".`,
        { campoAlterado: 'Certificado', valorAnterior: 'Não emitido', valorNovo: 'Emitido', municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return path;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [participantes, formacoes, userEmail]);

  const removerCertificadoParticipante = useCallback(async (
    participanteId: string,
    url: string,
  ): Promise<boolean> => {
    setSaving(true);
    try {
      await supabase.storage.from('formacao-documentos').remove([url]);
      const { error: dbErr } = await supabase
        .from('formacao_participantes')
        .update({ certificado_url: null })
        .eq('id', participanteId);
      if (dbErr) throw dbErr;
      setParticipantes((prev) =>
        prev.map((p) => p.id === participanteId ? { ...p, certificado_url: null } : p)
      );

      const p = participantes.find((x) => x.id === participanteId);
      const formacaoNome = formacoes.find((f) => f.id === p?.formacao_id)?.nome ?? p?.formacao_id ?? '';
      await registarAlteracao(
        'participante' as EntidadeAuditada,
        participanteId,
        p?.funcionario_nome ?? participanteId,
        'edicao',
        `Certificado removido de "${p?.funcionario_nome ?? participanteId}" na formação "${formacaoNome}".`,
        { campoAlterado: 'Certificado', valorAnterior: 'Emitido', valorNovo: 'Removido', municipioId: MUNICIPIO_FORMACAO_ID, modulo: 'formacao', userEmail }
      );

      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [participantes, formacoes, userEmail]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const participantesDaFormacao = useCallback(
    (formacaoId: string) => participantes.filter((p) => p.formacao_id === formacaoId),
    [participantes]
  );

  const documentosDaFormacao = useCallback(
    (formacaoId: string) => documentos.filter((d) => d.formacao_id === formacaoId),
    [documentos]
  );

  return (
    <FormacaoContext.Provider value={{
      formacoes, participantes, documentos, formadores,
      loading, saving, error,
      criarFormacao, editarFormacao, eliminarFormacao,
      adicionarParticipante, atualizarParticipante, removerParticipante,
      participantesDaFormacao, documentosDaFormacao,
      uploadDocumento, eliminarDocumento, getDocumentoUrl,
      uploadCertificadoParticipante, removerCertificadoParticipante,
      refresh: async () => {
        loadedRef.current = false;
        await loadAll();
        loadedRef.current = true;
      },
    }}>
      {children}
    </FormacaoContext.Provider>
  );
}

export function useFormacao(): FormacaoContextValue {
  const ctx = useContext(FormacaoContext);
  if (!ctx) throw new Error('useFormacao must be used within FormacaoProvider');
  return ctx;
}